/** Saved Blender sculpts, shared by creation, customization and wardrobe fitting. */
export const BODY_BUILDS = [
  { id: 'balanced', label: 'Balanced', description: 'Even, natural proportions.' },
  { id: 'slender', label: 'Slender', description: 'A lean frame and narrower silhouette.' },
  { id: 'powerful', label: 'Powerful', description: 'Broad shoulders and defined muscle.' },
  { id: 'stocky', label: 'Stocky', description: 'A solid frame and fuller proportions.' },
] as const;
export type BodyBuild = typeof BODY_BUILDS[number]['id'];
export function validateBodyBuild(raw: unknown): BodyBuild {
  return BODY_BUILDS.find(build => build.id === raw)?.id ?? 'balanced';
}

/** Body proportions are independent of the authored head selection. */
export const BODY_SHAPE_AXES = {
  stature: 'Stature', mass: 'Body mass', muscle: 'Muscle', waist: 'Waist', hips: 'Hips', chest: 'Chest',
} as const;
export type BodyShapeAxis = keyof typeof BODY_SHAPE_AXES;
export type BodyShape = Record<BodyShapeAxis, number>;
export const DEFAULT_BODY_SHAPE: Readonly<BodyShape> = Object.freeze({
  stature: 0, mass: 0, muscle: 0, waist: 0, hips: 0, chest: 0,
});
/** Unknown legacy facial-slider fields are discarded when a save is read. */
export function validateBodyShape(raw: unknown): BodyShape {
  const value = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {};
  return Object.fromEntries(Object.keys(BODY_SHAPE_AXES).map(key => {
    const v = value[key];
    return [key, typeof v === 'number' && Number.isFinite(v) ? Math.max(-1, Math.min(1, v)) : 0];
  })) as BodyShape;
}
export const BODY_ARCHETYPES = [
  { id: 'classic', male: 'Classic', female: 'Classic', shape: {} },
  { id: 'lean', male: 'Young lean', female: 'Young petite', shape: { stature: -0.65, mass: -0.65, muscle: -0.3 } },
  { id: 'tall', male: 'Tall lean', female: 'Tall lean', shape: { stature: 1, mass: -0.45, waist: -0.25 } },
  { id: 'athletic', male: 'Athletic', female: 'Athletic', shape: { muscle: 0.85, waist: -0.35, chest: 0.25 } },
  { id: 'full', male: 'Broad heavy', female: 'Curvy', shape: { mass: 0.85, hips: 0.55, chest: 0.45, waist: 0.3 } },
  { id: 'mature', male: 'Mature', female: 'Mature', shape: { mass: 0.25, waist: 0.35 } },
  { id: 'elder', male: 'Elder', female: 'Elder', shape: { stature: -0.35, muscle: -0.5, mass: -0.2, waist: 0.2 } },
] as const;
export function bodyShapeKey(raw: unknown): string {
  return Object.values(validateBodyShape(raw)).join(',');
}
